import gql from 'graphql-tag';
import { clients } from '../../util/clients';
import { bulkResult } from '../../util/shared/graphql-global';
import { getWorkstationNumber } from '../../util/workstation-util';

const { auth } = clients.direct;
const { backendRest } = clients.direct;

const logConfigAudit = async ({
  dispatch,
  didPersist,
  calledScope,
  previousItem,
  item,
  upsertDoc,
  isLogAudit,
}) => {
  if (!didPersist || !isLogAudit || !previousItem) return;

  const savedItem = { ...item };
  if (upsertDoc.insertedCount === 1) {
    savedItem.id = upsertDoc.insertedIds['0'];
  }

  await dispatch('audit/logAudit', {
    upsert: {
      program: 'SystemConfig',
      type: calledScope,
      subtype: upsertDoc.insertedCount === 1 ? 'C' : 'U',
      identifier: savedItem.id ? String(savedItem.id) : null,
      beforeSave: previousItem,
      afterSave: savedItem,
    },
  }, { root: true });
};

export const SystemConfigActions = {
  async loadConfig({ dispatch, commit }, { requiredScope, isSystemConfig }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const scope = requiredScope;
      results = await auth.query({
        query: gql` query loadConfig($scope: String) {
          configs(scope: $scope) { id scope configuration }
        }`,
        variables: {
          scope,
        },
        fetchPolicy: 'no-cache',
      });
      let configData = [];
      if (results.data.configs !== null) {
        configData = results.data.configs;
      }
      if (!isSystemConfig) {
        commit('setSettingItem', { items: configData, scope: requiredScope });
      }
      return configData;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading Configs.', { root: true });
      return [];
    }
  },
  async updateConfig(
    { dispatch, commit },
    {
      item,
      calledScope,
      previousItem,
      isLogAudit,
    },
  ) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const itemData = { ...item };
      const itemId = itemData.id;
      delete itemData.id;
      const updates = [{
        id: itemId,
        scope: calledScope,
        configuration: itemData,
      }];
      results = await auth.mutate({
        mutation: gql`mutation upsertConfig ($transaction: [SystemConfigBulkInput])  {
          upsertConfig(upserts: $transaction ) { ${bulkResult} }
        }`,
        variables: { transaction: updates },
      });
      const upsertDoc = results.data.upsertConfig;
      // #migrateTaxUnit
      if (upsertDoc) {
        if (item.useNewTaxUnitSetup !== undefined) {
          await dispatch('updateBackendTaxUnitSetup', item.useNewTaxUnitSetup);
        }
        let didPersist = false;
        if (upsertDoc.modifiedCount === 1) {
          commit('setAfterUpdate', { data: item, scope: calledScope, newRecordID: null }); // newRecordID 'null' means modified the exisiting record.
          dispatch('flashSuccess', 'Configuration Successfully Updated', { root: true });
          didPersist = true;
        } else if (upsertDoc.insertedCount === 1) {
          commit('setAfterUpdate', { data: item, scope: calledScope, newRecordID: upsertDoc.insertedIds['0'] });
          dispatch('flashSuccess', 'Configuration Successfully Added', { root: true });
          didPersist = true;
        } else {
          dispatch('flashInfo', 'Nothing to Update', { root: true });
        }
        await logConfigAudit({
          dispatch,
          didPersist,
          calledScope,
          previousItem,
          item,
          upsertDoc,
          isLogAudit,
        });
        return didPersist;
      }
      return false;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem on saving configuration.', { root: true });
      return false;
    }
  },
  async upsertConfigSpecificFields({ dispatch, commit }, { item, calledScope }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const itemData = { ...item };
      const itemId = itemData.id;
      delete itemData.id;
      const updates = [{
        id: itemId,
        scope: calledScope,
        configuration: itemData,
      }];
      results = await auth.mutate({
        mutation: gql`mutation upsertConfigSpecificFields ($transaction: [SystemConfigBulkInput])  {
          upsertConfigSpecificFields(upserts: $transaction ) { ${bulkResult} }
        }`,
        variables: { transaction: updates },
      });
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem on saving configuration.', { root: true });
    }
  },
  async getGlobalSettingStore({ commit, dispatch }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`{
          getGlobalSettingStore{ configuration }
        }`,
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      const data = results.data.getGlobalSettingStore;
      return data;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting Global settings store.', { root: true });
    }
    return {};
  },
  async updateBackendTaxUnitSetup({ dispatch }, useNewTaxUnitSetup) {
    try {
      const response = await backendRest.post('/systemconfig/update-tax-unit-setup', {
        useNewTaxUnitSetup,
      });
      if (response.data.success) {
        dispatch('flashSuccess', 'Backend Tax Unit Setup updated successfully', { root: true });
      } else {
        dispatch('flashError', response.data.message, { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      dispatch('flashError', 'Failed to update Tax Unit setup in the backend', { root: true });
    }
  },
  fetchAndSetWkst({ commit }, item) {
    const updatedItem = { ...item };
    /*
      Should override item's values as per need
      Worstation ID fetching from localstorage
    */
    updatedItem.wkst = updatedItem.wkst || getWorkstationNumber();
    commit('setWorkstationItem', updatedItem);
    return updatedItem;
  },
};

export default SystemConfigActions;
